import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { searchMembers } from '../../../controllers/memberManagementController';
import { createMemberMeal } from '../../../controllers/meetingMealController';
import { fetchWeeklyMeetingConfig } from '../../../controllers/meetingsController';
import { buildMealFormDefaults, formatPeriodLabel, toApiDateTime } from '../meetingMealsFormUtils';
import { formatPaymentOperationError } from '../../../utils/logoPrerequisiteErrors';

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '5px' },
  '& .MuiInputBase-input': { fontSize: '14px' },
};

const CreateMemberMealDialog = ({
  open,
  onClose,
  onCreated,
  prefillMemberId = null,
  prefillMemberName = null,
}) => {
  const navigate = useNavigate();
  const [memberQuery, setMemberQuery] = useState('');
  const [memberOptions, setMemberOptions] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [form, setForm] = useState({
    period: '',
    title: '',
    amount: '',
    dueDate: '',
    finalDueDate: '',
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setSubmitting(false);
    (async () => {
      try {
        const config = await fetchWeeklyMeetingConfig();
        setForm(buildMealFormDefaults(config));
      } catch {
        setForm(buildMealFormDefaults(null));
      }
    })();
    if (prefillMemberId) {
      setSelectedMember({ id: prefillMemberId, fullName: prefillMemberName || prefillMemberId });
    } else {
      setSelectedMember(null);
      setMemberQuery('');
      setMemberOptions([]);
    }
  }, [open, prefillMemberId, prefillMemberName]);

  useEffect(() => {
    if (!open || prefillMemberId) return;
    if (!memberQuery || memberQuery.trim().length < 2) {
      setMemberOptions([]);
      return;
    }
    let active = true;
    setMemberLoading(true);
    searchMembers(memberQuery.trim())
      .then((data) => { if (active) setMemberOptions(Array.isArray(data) ? data : []); })
      .finally(() => { if (active) setMemberLoading(false); });
    return () => { active = false; };
  }, [memberQuery, open, prefillMemberId]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'period' && value) {
        next.title = `${formatPeriodLabel(value)} — ek borç`;
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    const memberId = selectedMember?.id;
    if (!memberId) {
      setError('Üye seçiniz');
      return;
    }
    if (!form.period?.trim()) {
      setError('Dönem zorunludur (YYYY-MM)');
      return;
    }
    if (!form.title?.trim()) {
      setError('Başlık zorunludur');
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      setError('Geçerli bir tutar giriniz');
      return;
    }
    if (!form.dueDate) {
      setError('Vade tarihi zorunludur');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const created = await createMemberMeal({
        memberId,
        period: form.period.trim(),
        title: form.title.trim(),
        amount: Number(form.amount),
        dueDate: toApiDateTime(form.dueDate),
        finalDueDate: form.finalDueDate ? toApiDateTime(form.finalDueDate) : null,
        description: form.description?.trim() || null,
      });
      toast.success('Yemek borcu oluşturuldu');
      onClose();
      if (onCreated) onCreated(created);
      if (created?.id) navigate(`/payments/weekly-meetings/${created.id}`);
    } catch (e) {
      setError(formatPaymentOperationError(e, { actionLabel: 'Yemek borcu oluşturma' }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '18px', fontWeight: 500, display: 'flex', justifyContent: 'space-between', pr: 3 }}>
        Tek Üyeye Yemek Borcu
        <IconButton onClick={onClose} size="small" disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{error}</Alert>}
          {prefillMemberId ? (
            <Box>
              <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>Üye</Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{prefillMemberName || prefillMemberId}</Typography>
            </Box>
          ) : (
            <Autocomplete
              options={memberOptions}
              loading={memberLoading}
              value={selectedMember}
              onChange={(_, value) => setSelectedMember(value)}
              onInputChange={(_, value) => setMemberQuery(value)}
              getOptionLabel={(o) => o?.fullName || o?.email || ''}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              renderInput={(params) => (
                <TextField {...params} label="Üye" placeholder="Ad ile ara..." sx={fieldSx} />
              )}
            />
          )}
          <TextField
            label="Dönem"
            type="month"
            value={form.period}
            onChange={handleChange('period')}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={fieldSx}
          />
          <TextField label="Başlık" value={form.title} onChange={handleChange('title')} fullWidth sx={fieldSx} />
          <TextField
            label="Tutar"
            type="number"
            inputProps={{ step: '0.01', min: 0.01 }}
            value={form.amount}
            onChange={handleChange('amount')}
            fullWidth
            sx={fieldSx}
          />
          <TextField
            label="Vade Tarihi"
            type="date"
            value={form.dueDate}
            onChange={handleChange('dueDate')}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={fieldSx}
          />
          <TextField
            label="Son Ödeme Tarihi"
            type="date"
            value={form.finalDueDate}
            onChange={handleChange('finalDueDate')}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={fieldSx}
          />
          <TextField
            label="Açıklama"
            value={form.description}
            onChange={handleChange('description')}
            multiline
            rows={2}
            fullWidth
            sx={fieldSx}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>İptal</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ textTransform: 'none', backgroundColor: '#0E4D94' }}
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Oluştur'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateMemberMealDialog;
